import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { config } from '../../config';
import CompetitionForm from './CompetitionForm';
import './CompetitionManagement.css';

interface Competition {
  id: string;
  name: string;
  competition_type?: string;
  tier: string;
  season_start: string;
  season_end: string;
  package_type_id?: string;
  package_name?: string;
  package_sessions?: number;
  package_price?: number;
  tshirt_cost: number;
  team_registration_fee: number;
  tournament_registration_fee: number;
  other_costs: number;
  other_costs_description?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const CompetitionManagement: React.FC = () => {
  const { user } = useAuth();
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState<Competition | null>(null);
  const [filterTier, setFilterTier] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchCompetitions();
  }, [filterTier, filterStatus]);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cognitoToken') || '';
      const params = new URLSearchParams();
      
      if (filterTier !== 'all') params.append('tier', filterTier);
      if (filterStatus !== 'all') params.append('status', filterStatus);

      const response = await fetch(`${config.BACKEND_URL}/api/competitions?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompetitions(data);
      } else {
        console.error('Failed to fetch competitions');
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompetition = () => {
    setSelectedCompetition(null);
    setShowForm(true);
  };

  const handleEditCompetition = (competition: Competition) => {
    setSelectedCompetition(competition);
    setShowForm(true);
  };

  const handleDeleteCompetition = async (competitionId: string) => {
    if (!window.confirm('Are you sure you want to delete this competition? This will also remove all student enrollments.')) {
      return;
    }

    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(`${config.BACKEND_URL}/api/competitions/${competitionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ Competition deleted successfully');
        fetchCompetitions();
      } else {
        const error = await response.json();
        alert(`❌ Failed to delete competition: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting competition:', error);
      alert('❌ An error occurred while deleting the competition');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedCompetition(null);
    fetchCompetitions();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusBadge = (status: string) => {
    const colors: { [key: string]: { bg: string; text: string } } = {
      upcoming: { bg: '#e3f2fd', text: '#1976d2' },
      active: { bg: '#e8f5e9', text: '#2e7d32' },
      completed: { bg: '#f3e5f5', text: '#7b1fa2' },
      cancelled: { bg: '#ffebee', text: '#c62828' }
    };
    
    const color = colors[status] || { bg: '#f5f5f5', text: '#666' };
    
    return (
      <span style={{
        padding: '4px 12px',
        backgroundColor: color.bg,
        color: color.text,
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold',
        textTransform: 'uppercase'
      }}>
        {status}
      </span>
    );
  };

  const getTierIcon = (tier: string) => {
    const icons: { [key: string]: string } = {
      foundations: '🌱',
      innovators: '🚀',
      pioneers: '🌟'
    };
    return icons[tier] || '📚';
  };

  return (
    <div className="competition-management">
      <div className="competition-header">
        <div>
          <h2>🏆 Competition Management</h2>
          <p>Manage FLL competitions, seasons, and pricing</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={handleCreateCompetition}
          style={{ padding: '12px 24px', fontSize: '16px' }}
        >
          + Create Competition
        </button>
      </div>

      {/* Filters */}
      <div className="competition-filters">
        <div className="filter-group">
          <label>Tier:</label>
          <select 
            value={filterTier} 
            onChange={(e) => setFilterTier(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Tiers</option>
            <option value="foundations">🌱 Foundations</option>
            <option value="innovators">🚀 Innovators</option>
            <option value="pioneers">🌟 Pioneers</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="upcoming">Upcoming</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading competitions...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && competitions.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🏆</div>
          <h3>No Competitions Found</h3>
          <p>Create your first competition to get started</p>
          <button 
            className="btn btn-primary"
            onClick={handleCreateCompetition}
          >
            + Create Competition
          </button>
        </div>
      )}

      {/* Competitions List */}
      {!loading && competitions.length > 0 && (
        <div className="competitions-grid">
          {competitions.map((competition) => (
            <div key={competition.id} className="competition-card">
              <div className="competition-card-header">
                <div className="competition-title">
                  <span className="competition-icon">🤖</span>
                  <h3>{competition.name}</h3>
                </div>
                {getStatusBadge(competition.status)}
              </div>

              <div className="competition-card-body">
                <div className="competition-info-row">
                  <span className="info-label">Tier:</span>
                  <span className="info-value">
                    {getTierIcon(competition.tier)} {competition.tier.charAt(0).toUpperCase() + competition.tier.slice(1)}
                  </span>
                </div>

                <div className="competition-info-row">
                  <span className="info-label">Season:</span>
                  <span className="info-value">
                    {formatDate(competition.season_start)} - {formatDate(competition.season_end)}
                  </span>
                </div>

                {competition.package_name && (
                  <div className="competition-info-row">
                    <span className="info-label">Session Pack:</span>
                    <span className="info-value">{competition.package_name} ({competition.package_sessions} sessions)</span>
                  </div>
                )}

                <div className="competition-pricing">
                  <div className="pricing-item">
                    <span className="pricing-label">Session Pack:</span>
                    <span className="pricing-value">${Number(competition.package_price || 0).toFixed(2)}</span>
                  </div>
                  
                  {competition.tshirt_cost > 0 && (
                    <div className="pricing-item">
                      <span className="pricing-label">T-Shirt:</span>
                      <span className="pricing-value">${Number(competition.tshirt_cost).toFixed(2)}</span>
                    </div>
                  )}
                  
                  {competition.team_registration_fee > 0 && (
                    <div className="pricing-item">
                      <span className="pricing-label">Team Reg (shared):</span>
                      <span className="pricing-value">${Number(competition.team_registration_fee).toFixed(2)}</span>
                    </div>
                  )}
                  
                  {competition.tournament_registration_fee > 0 && (
                    <div className="pricing-item">
                      <span className="pricing-label">Tournament (shared):</span>
                      <span className="pricing-value">${Number(competition.tournament_registration_fee).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="competition-card-footer">
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleEditCompetition(competition)}
                >
                  ✏️ Edit
                </button>
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteCompetition(competition.id)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Competition Form Modal */}
      {showForm && (
        <CompetitionForm
          competition={selectedCompetition}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowForm(false);
            setSelectedCompetition(null);
          }}
        />
      )}
    </div>
  );
};

export default CompetitionManagement;

